import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import {
  Action,
  Alert,
  Label,
  Modal,
  SwitchInput,
  TextInput,
  MultiSelect,
} from 'corso-admin-ui';
import {
  CrewMerchantUi,
  ReportableEntity,
  RollingDatePreset,
} from 'corso-types';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import api from '~/api';
import DescriptionList from '~/components/DescriptionList';
import { useSearchState } from '~/components/searchTable/searchControlBar/SearchContext';
import { useCustomFields } from '~/features/settings/hooks/useCustomFields';
import { useReportDefinitionUpsert } from '~/features/settings/hooks/useReportDefinitions';
import { useToast } from '~/hooks/useToast';
import { useStoreId } from '~/providers/StoreUserProvider';
import { useUser } from '~/providers/UserProvider';
import {
  columnsByEntity,
  exportColumnDisplayName,
  filtersToDescriptions,
  isClaimEntity,
  pluralEntityName,
} from '~/utils/dataExport';
import { isErrorMessage } from '~/utils/errorResponse';
import { numberIdsToOptions, optionsToNumberIds } from '~/utils/form';

/*
 * Covers all kinds. Per-kind column correctness is enforced by
 * `reportReqSchema.parse()` in the mutation, while the UI constrains
 * which columns the user can select.
 */
const reportFormSchema = z.object({
  filename: z.string().optional(),
  columns: z.array(z.string()),
  lineItemCustomFieldIds: z.array(z.number()),
  inspectionCustomFieldIds: z.array(z.number()),
  registrationCustomFieldIds: z.array(z.number()),
});

type ReportFormValues = z.infer<typeof reportFormSchema>;

export function DataExportForm({
  show,
  onClose,
  entity,
}: {
  show: boolean;
  onClose: () => void;
  entity: ReportableEntity;
}) {
  const user = useUser();
  const storeId = useStoreId();
  const [state] = useSearchState();
  const toast = useToast();

  const { data: customFields = [] } = useCustomFields();

  const [saveAsTemplate, setSaveAsTemplate] = useState(false);

  const viewFilters = state.view.filters;
  const hasDateFilter = 'createdOn' in viewFilters && !!viewFilters.createdOn;

  // Resolve once so the run + the saved template both persist the same filter
  // shape (with the last-30-days preset injected when the merchant didn't pick
  // a date).
  const resolvedFilters = hasDateFilter
    ? viewFilters
    : {
        ...viewFilters,
        createdOn: {
          kind: 'preset' as const,
          preset: RollingDatePreset.last30Days,
        },
      };

  const { mutate: runReport, isPending } = useMutation({
    onSuccess: () =>
      toast.show(
        'Export started. You will receive an email when the CSV is ready.',
      ),
    onError: (err: unknown) => {
      toast.show(
        isErrorMessage(err)
          ? err.message
          : 'Failed to start export. Please try again.',
      );
    },
    mutationFn: (values: ReportFormValues) => {
      const body = CrewMerchantUi.reportInlineRun.parse({
        kind: entity,
        filters: resolvedFilters,
        jsonDefinition: values,
      });
      return api.store(storeId).reports.runInline(body);
    },
  });

  const columns = Object.values(columnsByEntity[entity]);

  const { mutate: upsertReportDefinition } = useReportDefinitionUpsert();

  const {
    formState: { errors },
    handleSubmit,
    control,
    register,
    reset,
  } = useForm({
    resolver: zodResolver(reportFormSchema),
    defaultValues: {
      filename: undefined,
      columns,
      lineItemCustomFieldIds: [],
      inspectionCustomFieldIds: [],
      registrationCustomFieldIds: [],
    },
  });

  function handleFormSubmission(values: ReportFormValues) {
    onClose();
    runReport(values);
    if (saveAsTemplate) {
      const name =
        values.filename ??
        `${pluralEntityName[entity]} Report ${new Date().toLocaleDateString()}`;
      const payload = CrewMerchantUi.reportDefinitionCreate.parse({
        kind: entity,
        name,
        filters: resolvedFilters,
        jsonDefinition: values,
      });
      upsertReportDefinition(payload);
    }
  }

  const columnOptions = columns.map((value) => ({
    label: exportColumnDisplayName[value],
    value,
  }));

  const descriptions = filtersToDescriptions(
    state.view.filters,
    state.filterDefinition,
    { includeSearchTerm: true },
  );

  const closeAndReset = () => {
    onClose();
    reset();
  };

  const customFieldOptions = customFields.map((cf) => ({
    label: cf.displayName,
    value: `${cf.id}`,
  }));

  const formId = `${entity.toLowerCase()}-report-form`;
  return (
    <Modal
      title={`Export ${pluralEntityName[entity]}`}
      show={show}
      onClose={closeAndReset}
      actions={
        <>
          <Action onClick={closeAndReset}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            form={formId}
            disabled={isPending}
            loading={isPending}
          >
            Export
          </Action>
        </>
      }
    >
      <form
        id={formId}
        onSubmit={(e) => {
          handleSubmit((values) => {
            handleFormSubmission(values);
          })(e).catch(() => undefined);
        }}
        className="flex flex-col gap-4"
      >
        <Alert
          variant="info"
          heading={`Your export will be emailed to ${user.email}`}
        />

        <TextInput
          label="Name"
          id="filename"
          {...register('filename')}
          error={errors.filename?.message ?? undefined}
        />

        {!hasDateFilter && (
          <Alert heading="Last 30 Days">
            No date filter applied, defaulting to last 30 days
          </Alert>
        )}

        {!!descriptions.length && (
          <>
            <span className="text-sm font-medium"> Filter Details</span>
            <DescriptionList descriptions={descriptions} />
          </>
        )}

        <Controller
          name="columns"
          control={control}
          render={({ field: { onChange, value }, fieldState }) => (
            <MultiSelect
              togglable
              label="Columns"
              placeholder="Select columns"
              options={columnOptions}
              value={value.map((option) => ({
                label:
                  columnOptions.find((o) => o.value === option)?.label ??
                  option,
                value: option,
              }))}
              onChange={(next) => onChange(next.map((v) => v.value))}
              error={fieldState.error?.message}
            />
          )}
        />

        {entity === 'Registration' && (
          <Controller
            name="registrationCustomFieldIds"
            control={control}
            render={({ field, fieldState }) => (
              <MultiSelect
                togglable
                label="Custom Field Responses - Registration"
                placeholder="Select custom fields"
                options={customFieldOptions}
                value={numberIdsToOptions(field.value, customFieldOptions)}
                onChange={(next) => field.onChange(optionsToNumberIds(next))}
                error={fieldState.error?.message}
              />
            )}
          />
        )}

        {isClaimEntity(entity) && (
          <>
            <Controller
              name="lineItemCustomFieldIds"
              control={control}
              render={({ field, fieldState }) => (
                <MultiSelect
                  togglable
                  label="Custom Field Responses - Customer Provided"
                  details="Custom Field responses collected in the Customer Portal."
                  placeholder="Select custom fields"
                  options={customFieldOptions}
                  value={numberIdsToOptions(field.value, customFieldOptions)}
                  onChange={(next) => field.onChange(optionsToNumberIds(next))}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Controller
              name="inspectionCustomFieldIds"
              control={control}
              render={({ field, fieldState }) => (
                <MultiSelect
                  togglable
                  label="Custom Field Responses - Inspection"
                  placeholder="Select custom fields"
                  options={customFieldOptions}
                  value={numberIdsToOptions(field.value, customFieldOptions)}
                  onChange={(next) => field.onChange(optionsToNumberIds(next))}
                  error={fieldState.error?.message}
                />
              )}
            />
          </>
        )}
      </form>

      <Alert variant="tip" heading="Save as a Template">
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor="save-as-template" className="cursor-pointer">
            Re-run and schedule future exports from the Data Export settings.
          </Label>
          <SwitchInput
            id="save-as-template"
            label="Save as template"
            labelVisuallyHidden
            checked={saveAsTemplate}
            onChange={(e) => {
              setSaveAsTemplate(e.target.checked);
            }}
          />
        </div>
      </Alert>
    </Modal>
  );
}
